import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { forkJoin, map, take } from 'rxjs';
import { DataService } from 'src/app/services/data/data.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ContractPartnerOspAssignment } from 'src/app/services/types/contract-partner.model';
import { CPBillingCode } from 'src/app/services/types/cp-billing-code.model';
import { Crew } from 'src/app/services/types/crew.model';
import { DailyReport } from 'src/app/services/types/daily-report.model';
import { OSP } from 'src/app/services/types/osp.model';

@Component({
  selector: 'daily-importer-old',
  templateUrl: './daily-importer-old.page.html',
  styleUrls: ['./daily-importer-old.page.scss'],
  providers: [DataService, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyImporterPage implements OnInit {
  ospLoaded: boolean = false;
  contractPartnerLoaded: boolean = false;
  billingCodesLoaded: boolean = false;
  dailyReportsLoaded: boolean = false;
  crewsLoaded: boolean = false;

  rid: string = '';

  osp: OSP;
  contractPartners: ContractPartnerOspAssignment[];
  billingCodes: CPBillingCode[];
  dailyReports: DailyReport[];
  crews?: Crew[];

  newDailyReports: DailyReport[] = [new DailyReport()];

  selectedDate: string;

  selectedContractPartner: ContractPartnerOspAssignment;

  serverDate: string;

  get minDate(): string {
    let date = new Date();
    date.setDate(date.getDate() - 14);
    return date.toISOString();
  }

  get maxDate(): string {
    let date = new Date();
    date.setDate(date.getDate() + 2);
    return date.toISOString();
  }

  get activeCrews(): Crew[] {
    if (!this.crews) {
      return [];
    }

    return this.crews.filter((crew) => crew.active);
  }

  canBeSaved(dr: DailyReport): boolean {
    return !dr.saved && (dr.qty || dr.qty == 0) && dr.workDate && dr.workDate.length != 0;
  }

  getContractPartnerName(dr: DailyReport):string{
    if(!dr || !dr.relatedContractPartner || !this.contractPartners||this.contractPartners.length ==0){
      return '';
    }

    const cpName = this.contractPartners.find(cp=>cp.relatedContractPartner == dr.relatedContractPartner);

    if(!cpName){
      return '';
    }

    return cpName.contractPartnerName;
}

  constructor(
    private dataService: DataService,
    private datePipe: DatePipe,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {
    this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe((params: Params) => {
        if (params['rid']) {
          this.rid = params['rid'];
        }
      });
  }

  ngOnInit() {
    this.getDailyReports();
    this.dataService.getOSP(this.rid).subscribe((data) => {
      this.osp = new OSP(data[0]);
      this.ospLoaded = true;

      this.cd.markForCheck();
      this.dataService
        .getContractPartners(
          this.osp.assignmentCPName,
          this.osp.clientNameAndProject
        )
        .subscribe((data) => {
          this.contractPartners = (data as []).map(
            (cpData) => new ContractPartnerOspAssignment(cpData)
          );
          this.contractPartnerLoaded = true;
          this.cd.markForCheck();
        });
    });
  }

  checkSelectedContractPartner(dr: DailyReport) {
    if (
      dr.relatedContractPartner ==
      this.selectedContractPartner.relatedContractPartner
    ) {
      return;
    }

    dr.relatedContractPartner =
      this.selectedContractPartner.relatedContractPartner;
    dr.crew = undefined;
    dr.cpBillingCode = undefined;

    this.cd.markForCheck();
  }

  onCrewChange(event, i: number) {
    const selectedCrew = event.detail.value as Crew;
    this.checkSelectedContractPartner(this.newDailyReports[i]);
    this.newDailyReports[i].crew = selectedCrew;
  }

  getDailyReports() {
    this.dataService.getDailyReports(this.rid).subscribe((data) => {
      this.dailyReports = (data as []).map((drData) => new DailyReport(drData));
      this.dailyReportsLoaded = true;
      this.cd.markForCheck();
    });
  }

  getCrews() {
    this.dataService
      .getCrews(this.selectedContractPartner.relatedContractPartner)
      .subscribe((data) => {
        this.crews = (data as []).map((c) => new Crew(c));
        this.crewsLoaded = true;
        this.cd.markForCheck();
      });
  }

  onContractPartnerChange(event) {
    this.billingCodesLoaded = false;
    this.crewsLoaded = false;
    this.selectedContractPartner = event.detail
      .value as ContractPartnerOspAssignment;

    this.getCrews();

    this.dataService
      .getCPBillingCodes(
        this.osp?.relatedProject,
        this.osp?.relatedClient,
        this.selectedContractPartner?.contractPartnerName
      )
      .subscribe((data) => {
        this.billingCodes = (data['data'] as []).map(
          (bcData) => new CPBillingCode(bcData)
        );
        
        const sd = new Date(data['dateTime']);
        sd.setHours(sd.getHours()-4);
        this.serverDate = sd.toISOString();

        if (this.osp.limitWorkDate) {
          this.newDailyReports
            .filter((fdr) => !fdr.saved)
            .forEach((dr) => {
              dr.workDate = this.serverDate;
            });
        }
        
        this.billingCodesLoaded = true;
        this.cd.markForCheck();
      });
  }

  onWorkDateChange(event, i: number) {
    this.newDailyReports[i].workDate = this.datePipe.transform(
      event.detail.value,
      'yyyy-MM-dd'
    );
  }

  transformDate(date: string) {
    const dateString = this.datePipe.transform(date, 'MM-dd-yyyy');
    return dateString;
  }

  onBillingCodeChange(event, i: number) {
    const selectedBillingCode = event.value as CPBillingCode;

    this.checkSelectedContractPartner(this.newDailyReports[i]);

    this.newDailyReports[i].cpBillingCode = selectedBillingCode;
  }

  switchNotes(dr: DailyReport) {
    dr.showNotes = !dr.showNotes;
  }

  onAddDailyReportClick() {
    this.newDailyReports.push(new DailyReport());
    
    if(!this.osp.limitWorkDate){
      this.newDailyReports[this.newDailyReports.length - 1].workDate =
      this.newDailyReports[this.newDailyReports.length - 2].workDate;
    }else{
      this.newDailyReports[this.newDailyReports.length - 1].workDate = this.serverDate;
    }

    this.newDailyReports[
      this.newDailyReports.length - 1
    ].relatedContractPartner =
      this.selectedContractPartner.relatedContractPartner;
    this.cd.markForCheck();
  }

  getDailyReportShortDescription(dr: DailyReport): string {
    if (dr.finalBillingCodeDescription) {
      return dr.finalBillingCodeDescription;
    }

    if (dr.description) {
      return dr.description;
    }

    if (dr.cpBillingCodeShortDescription) {
      return dr.cpBillingCodeShortDescription;
    }

    if (!this.billingCodes) {
      return '';
    }

    const bc = this.billingCodes.find(
      (bc) => bc.recordId == dr.cpBillingCodeId
    );

    if (bc) {
      return bc.billingCodeShortDescription;
    }

    return '';
  }

  onPostClick() {
    const unsavedDRs = this.newDailyReports.filter((dr) => this.canBeSaved(dr));

    if (!unsavedDRs || unsavedDRs.length == 0) {
      this.toastService.presentToast(
        'Error',
        'Please fill in at least one Daily Report',
        'top',
        'danger',
        4000
      );
      return;
    }

    const postDRRequests = [];

    unsavedDRs.forEach((dr) => {
      postDRRequests.push(this.saveDailyReport(dr));
    });

    forkJoin(...postDRRequests).subscribe((res) => {
      if (res && res.findIndex((r) => r == false)>=0) {
        this.toastService.presentToast(
          'Error',
          'Error while saving',
          'top',
          'danger',
          4000
        );
      } else {
        this.toastService.presentToast(
          'Success',
          `Daily Report${unsavedDRs.length > 1 ? 's were' : ''} Saved`,
          'top',
          'success',
          4000
        );
      }

      this.cd.markForCheck();
      this.getDailyReports();
    });
  }

  saveDailyReport(dr: DailyReport) {
    const postDRObservable = this.dataService
      .postDailyReport(this.rid, {
        workDate: this.datePipe.transform(dr.workDate, 'yyyy-MM-dd'),
        selectedContractPartner: dr.relatedContractPartner,
        selectedBillingCode: dr.cpBillingCode.recordId,
        relatedCrew: dr.crew?.rid,
        quantity: dr.qty,
        notes: dr.description,
      })
      .pipe(map((res) => {
        if (res['success']) {
          dr.saved = true;
          return true;
        }

        return false;
      }));

    return postDRObservable;
  }

  highlightedDates = (isoString) => {
    const date = new Date(isoString);
    const utcDay = date.getUTCDate();

    if (utcDay % 5 === 0) {
      return {
        textColor: '#800080',
        backgroundColor: '#ffc0cb',
      };
    }

    if (utcDay % 3 === 0) {
      return {
        textColor: 'var(--ion-color-secondary-contrast)',
        backgroundColor: 'var(--ion-color-secondary)',
      };
    }

    return undefined;
  };
}
